import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AppImage from '../../../components/AppImage';

const CustomerManagement = ({ customers, bookings }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredCustomers = customers?.filter((customer) => {
    const matchesSearch = 
      customer?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      customer?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesFilter = filterStatus === 'all' || customer?.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getCustomerBookings = (customerId) => {
    return bookings?.filter(booking => 
      booking?.customerName === customers?.find(c => c?.id === customerId)?.name
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      vip: { 
        label: 'VIP', 
        className: 'bg-warning/10 text-warning border-warning/20' 
      },
      regular: { 
        label: 'Regular', 
        className: 'bg-primary/10 text-primary border-primary/20' 
      },
      new: { 
        label: 'Novo', 
        className: 'bg-success/10 text-success border-success/20' 
      }
    };
    
    const config = statusConfig?.[status] || statusConfig?.regular;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const CustomerDetailModal = ({ customer, onClose }) => {
    if (!customer) return null;

    const customerBookings = getCustomerBookings(customer?.id);
    const upcomingBookings = customerBookings?.filter(b => 
      new Date(b?.date) >= new Date() && b?.status === 'confirmed'
    );
    const pastBookings = customerBookings?.filter(b => 
      new Date(b?.date) < new Date()
    );

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevation-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-heading font-bold text-card-foreground">
                Perfil do Cliente
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                iconName="X"
                iconSize={16}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Customer Info */}
              <div className="lg:col-span-1 space-y-6">
                <div className="text-center">
                  <AppImage
                    src={customer?.avatar}
                    alt={customer?.avatarAlt}
                    className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
                  />
                  <h4 className="text-xl font-heading font-bold text-card-foreground">
                    {customer?.name}
                  </h4>
                  <p className="text-muted-foreground">{customer?.email}</p>
                  <p className="text-muted-foreground">{customer?.phone}</p>
                  <div className="mt-2">
                    {getStatusBadge(customer?.status)}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h5 className="font-semibold text-card-foreground mb-3">Estatísticas</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Total de reservas:</span>
                        <span className="text-sm font-medium">{customer?.totalBookings}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Total gasto:</span>
                        <span className="text-sm font-medium">
                          R$ {customer?.totalSpent?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Avaliação média:</span>
                        <div className="flex items-center space-x-1">
                          <span className="text-sm font-medium">{customer?.averageRating}</span>
                          <Icon name="Star" size={12} className="text-warning" />
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Cliente desde:</span>
                        <span className="text-sm font-medium">
                          {new Date(customer?.joinDate)?.toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="MessageCircle"
                      iconPosition="left"
                      iconSize={16}
                      className="flex-1"
                    >
                      Contatar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="FileText"
                      iconPosition="left"
                      iconSize={16}
                      className="flex-1"
                    >
                      Relatório
                    </Button>
                  </div>
                </div>
              </div>

              {/* Booking History */}
              <div className="lg:col-span-2 space-y-6">
                {/* Upcoming Bookings */}
                <div>
                  <h5 className="font-semibold text-card-foreground mb-3">
                    Próximas Reservas ({upcomingBookings?.length || 0})
                  </h5>
                  {upcomingBookings?.length > 0 ? (
                    <div className="space-y-3">
                      {upcomingBookings?.slice(0, 3)?.map((booking) => (
                        <div key={booking?.id} className="p-3 bg-success/5 border border-success/20 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-card-foreground">
                                {booking?.courtName}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {new Date(booking?.date)?.toLocaleDateString('pt-BR')} • {booking?.time}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-success">
                                R$ {booking?.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      Nenhuma reserva próxima
                    </div>
                  )}
                </div>

                {/* Booking History */}
                <div>
                  <h5 className="font-semibold text-card-foreground mb-3">
                    Histórico de Reservas
                  </h5>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {pastBookings?.map((booking) => (
                      <div key={booking?.id} className="p-3 bg-muted/20 border border-muted/30 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-card-foreground">
                              {booking?.courtName}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {new Date(booking?.date)?.toLocaleDateString('pt-BR')} • {booking?.time}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-card-foreground">
                              R$ {booking?.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {booking?.players} jogadores
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Customer Reviews */}
                <div>
                  <h5 className="font-semibold text-card-foreground mb-3">
                    Avaliações Recentes
                  </h5>
                  <div className="space-y-3">
                    <div className="p-3 bg-muted/20 border border-muted/30 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center">
                            {[...Array(5)]?.map((_, i) => (
                              <Icon 
                                key={i}
                                name="Star" 
                                size={14} 
                                className={i < 5 ? "text-warning" : "text-muted"} 
                              />
                            ))}
                          </div>
                          <span className="text-sm font-medium">Quadra 1 - Society</span>
                        </div>
                        <span className="text-xs text-muted-foreground">15/10/2025</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        "Excelente quadra, muito bem cuidada e com boa iluminação. 
                        Recomendo para peladas noturnas!"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">
            Gestão de Clientes
          </h2>
          <p className="text-muted-foreground">
            Acompanhe perfis, histórico de reservas e avaliações
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <Input
            type="text"
            placeholder="Buscar clientes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-64"
            iconName="Search"
            iconSize={16}
          />
          
          <Select
            value={filterStatus}
            onChange={(value) => setFilterStatus(value)}
            className="w-32"
          >
            <option value="all">Todos</option>
            <option value="vip">VIP</option>
            <option value="regular">Regulares</option>
            <option value="new">Novos</option>
          </Select>
        </div>
      </div>
      {/* Customer Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Users" size={20} className="text-primary" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                {customers?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Total de clientes</div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-warning/10 rounded-lg">
              <Icon name="Crown" size={20} className="text-warning" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                {customers?.filter(c => c?.status === 'vip')?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Clientes VIP</div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-success/10 rounded-lg">
              <Icon name="UserPlus" size={20} className="text-success" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                {customers?.filter(c => c?.status === 'new')?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Novos este mês</div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-info/10 rounded-lg">
              <Icon name="Star" size={20} className="text-info" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                4.7
              </div>
              <div className="text-sm text-muted-foreground">Avaliação média</div>
            </div>
          </div>
        </div>
      </div>
      {/* Customers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCustomers?.map((customer) => {
          const customerBookings = getCustomerBookings(customer?.id);
          const upcomingBookings = customerBookings?.filter(b => 
            new Date(b?.date) >= new Date() && b?.status === 'confirmed'
          );

          return (
            <div key={customer?.id} className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
              {/* Customer Header */}
              <div className="flex items-center space-x-4 mb-4">
                <AppImage
                  src={customer?.avatar}
                  alt={customer?.avatarAlt}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold text-card-foreground">
                      {customer?.name}
                    </h3>
                    {getStatusBadge(customer?.status)}
                  </div>
                  <p className="text-sm text-muted-foreground">{customer?.email}</p>
                  <p className="text-sm text-muted-foreground">{customer?.phone}</p>
                </div>
              </div>
              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <div className="text-lg font-heading font-bold text-card-foreground">
                    {customer?.totalBookings}
                  </div>
                  <div className="text-xs text-muted-foreground">Reservas</div>
                </div>
                <div className="text-center p-3 bg-muted/30 rounded-lg">
                  <div className="text-lg font-heading font-bold text-card-foreground">
                    R$ {(customer?.totalSpent / 1000)?.toFixed(1)}k
                  </div>
                  <div className="text-xs text-muted-foreground">Total gasto</div>
                </div>
              </div>
              {/* Rating */}
              <div className="flex items-center justify-center space-x-2 mb-4">
                <div className="flex items-center">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon 
                      key={i}
                      name="Star" 
                      size={16} 
                      className={i < Math.floor(customer?.averageRating) ? "text-warning" : "text-muted"} 
                    />
                  ))}
                </div>
                <span className="text-sm font-medium text-card-foreground">
                  {customer?.averageRating}
                </span>
              </div>
              {/* Upcoming Bookings */}
              <div className="mb-4">
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  Próximas reservas: {upcomingBookings?.length || 0}
                </div>
                {upcomingBookings?.slice(0, 1)?.map((booking) => (
                  <div key={booking?.id} className="p-2 bg-primary/10 border border-primary/20 rounded text-sm">
                    <div className="font-medium text-primary">{booking?.courtName}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(booking?.date)?.toLocaleDateString('pt-BR')} • {booking?.time}
                    </div>
                  </div>
                ))}
              </div>
              {/* Actions */}
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedCustomer(customer)}
                  iconName="Eye"
                  iconPosition="left"
                  iconSize={14}
                  className="flex-1"
                >
                  Ver Perfil
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="MessageCircle"
                  iconSize={14}
                />
              </div>
            </div>
          );
        })}
      </div>
      {filteredCustomers?.length === 0 && (
        <div className="text-center py-12">
          <div className="flex items-center justify-center w-16 h-16 bg-muted/50 rounded-lg mx-auto mb-4">
            <Icon name="Users" size={24} className="text-muted-foreground" />
          </div>
          <h3 className="font-heading font-semibold text-card-foreground mb-2">
            Nenhum cliente encontrado
          </h3>
          <p className="text-muted-foreground">
            Ajuste os filtros ou adicione novos clientes
          </p>
        </div>
      )}
      {/* Customer Detail Modal */}
      <CustomerDetailModal
        customer={selectedCustomer}
        onClose={() => setSelectedCustomer(null)}
      />
    </div>
  );
};

export default CustomerManagement;